import React from 'react';
import { Box, Container, Grid, Typography, Stack, Chip, useMediaQuery, useTheme } from '@mui/material';
import CountUp from 'react-countup';
import VisibilitySensor from 'react-visibility-sensor';

// Visual Stat Card Component - Mobile Optimized
const VisualStatCard = ({ 
  icon, 
  value, 
  symbol, 
  sign, 
  title, 
  subtitle, 
  iconBg, 
  numberColor 
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        background: 'white',
        borderRadius: 4,
        p: { xs: 3, sm: 4 },
        textAlign: 'center',
        border: '2px solid transparent',
        transition: 'all 0.3s ease',
        position: 'relative',
        overflow: 'hidden',
        minHeight: { xs: 200, sm: 220, md: 260 },
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: { xs: 'scale(1.02)', md: 'translateY(-8px)' },
          boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
          borderColor: numberColor + '40',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '4px',
          background: `linear-gradient(90deg, ${numberColor}, ${numberColor}80)`,
        }
      }}
    >
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Box
          sx={{
            width: { xs: 50, sm: 55, md: 60 },
            height: { xs: 50, sm: 55, md: 60 },
            borderRadius: 3,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: { xs: '0 auto 12px', md: '0 auto 16px' },
            fontSize: { xs: 24, sm: 26, md: 28 },
            background: iconBg,
          }}
        >
          {icon}
        </Box>
        
        <VisibilitySensor partialVisibility offset={{ bottom: 20 }}>
          {({ isVisible }) => (
            <Typography
              variant='h2'
              fontWeight={700}
              fontSize={{ xs: 28, sm: 32, md: 36, lg: 44 }}
              sx={{ 
                color: numberColor,
                mb: 1,
                fontFamily: 'monospace',
                lineHeight: 1
              }}>
              {sign}
              {isVisible ? <CountUp end={value} duration={2} /> : value}
              {symbol}
            </Typography>
          )}
        </VisibilitySensor>

        <Typography
          variant='h6'
          fontSize={{ xs: 14, sm: 15, md: 16 }}
          fontWeight={600}
          sx={{ mb: 0.5, color: '#1a202c', lineHeight: 1.2 }}>
          {title}
        </Typography>
        
        <Typography
          variant='body2'
          fontSize={{ xs: 11, sm: 12, md: 13 }}
          color='text.secondary'
          sx={{ 
            opacity: 0.8, 
            mb: { xs: 1.5, md: 2 }, 
            lineHeight: 1.3,
            flex: 1
          }}>
          {subtitle}
        </Typography>
      </Box>
    </Box>
  );
};

// Quick Stat Pill Component - Mobile Optimized
const QuickStatPill = ({ number, text, color }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        background: `linear-gradient(135deg, ${color}20, ${color}10)`,
        border: `2px solid ${color}30`,
        borderRadius: 3,
        p: { xs: 2.5, sm: 3 },
        textAlign: 'center',
        transition: 'all 0.3s ease',
        minHeight: { xs: 100, sm: 120 },
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        '&:hover': {
          transform: 'scale(1.05)',
          boxShadow: `0 8px 25px ${color}30`,
        }
      }}
    >
      <Typography
        variant='h4'
        fontWeight={700}
        fontSize={{ xs: 20, sm: 24, md: 28 }}
        sx={{ color, mb: 1, lineHeight: 1 }}>
        {number}
      </Typography>
      <Typography
        variant='body2'
        fontSize={{ xs: 12, sm: 14 }}
        color='text.secondary'
        sx={{ 
          fontWeight: 500,
          lineHeight: 1.3,
          px: { xs: 0.5, sm: 0 }
        }}>
        {text}
      </Typography>
    </Box>
  );
};

export default function Stats() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  return (
    <Box
      sx={{
        py: { xs: 6, sm: 8, md: 12 },
        background: 'linear-gradient(135deg, #f1f5f9 0%, #e2e8f0 100%)',
      }}>
      <Container>
        {/* Section Header */}
        <Stack
          mb={{ xs: 4, sm: 5, md: 6 }}
          spacing={2}
          alignItems='center'
          justifyContent='center'>
          <Chip
            label='🚀 Proven Results'
            sx={{
              color: 'primary.main',
              bgcolor: 'rgba(0, 85, 255, 0.12)',
              fontSize: { xs: 12, sm: 13 },
              fontWeight: 600,
              px: { xs: 1.5, sm: 2 },
            }}
          />
          <Typography
            variant='h2'
            fontWeight={700}
            textAlign='center'
            fontSize={{ xs: 24, sm: 28, md: 32 }}
            color='#1a202c'
            sx={{ px: { xs: 2, sm: 0 } }}>
            Launch Smart, Grow Fast
          </Typography>
          <Typography
            variant='body1'
            fontSize={{ xs: 14, sm: 16 }}
            textAlign='center'
            color='#64748b'
            sx={{ 
              maxWidth: '500px', 
              mx: 'auto',
              px: { xs: 2, sm: 1, md: 0 }
            }}>
            Real numbers from businesses like yours
          </Typography>
        </Stack>

        {/* Main Stats Grid - Mobile Responsive */}
        <Grid container spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ mb: { xs: 5, md: 6 } }}>
          {statsData.map((item) => (
            <Grid key={item.id} item xs={6} sm={6} md={3}>
              <VisualStatCard
                icon={item.icon}
                value={item.value}
                symbol={item.symbol}
                sign={item.sign}
                title={item.title}
                subtitle={item.subtitle}
                iconBg={item.iconBg}
                numberColor={item.numberColor}
              />
            </Grid>
          ))}
        </Grid>

        {/* Quick Benefits Section - Mobile Optimized */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            p: { xs: 4, sm: 5 },
            borderRadius: 4,
            textAlign: 'center',
            mb: { xs: 4, md: 5 }
          }}
        >
          <Typography
            variant='h5'
            fontWeight={600}
            fontSize={{ xs: 18, sm: 20, md: 24 }}
            sx={{ mb: 1 }}>
            Why New Businesses Choose Us
          </Typography>
          <Typography
            variant='body1'
            fontSize={{ xs: 14, sm: 16 }}
            sx={{ 
              opacity: 0.9, 
              mb: { xs: 3, md: 4 }, 
              maxWidth: '600px', 
              mx: 'auto',
              px: { xs: 1, sm: 0 }
            }}>
            Join thousands who launched with AI tools + community power
          </Typography>
          
          <Grid container spacing={{ xs: 2, sm: 3 }}>
            <Grid item xs={12} sm={4}>
              <QuickStatPill
                number="54%"
                text="Marketing boost from word-of-mouth"
                color="#ffffff"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <QuickStatPill
                number="30%"
                text="Cost reduction with AI"
                color="#ffffff"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <QuickStatPill
                number="76%"  
                text="Visit after local search"
                color="#ffffff"
              />
            </Grid>
          </Grid>
        </Box>

        {/* Success Metrics Section - Mobile Optimized */}
        <Box
          sx={{
            textAlign: 'center',
            p: { xs: 3, sm: 4 },
            background: 'white',
            borderRadius: 4,
            border: '1px solid #e2e8f0'
          }}
        >
          <Typography
            variant='h6'
            fontWeight={600}
            fontSize={{ xs: 16, sm: 18, md: 20 }}
            sx={{ mb: 2, color: '#1a202c' }}>
            Ready to Join Them?
          </Typography>
          <Typography
            variant='body1'
            color='text.secondary'
            fontSize={{ xs: 14, sm: 16 }}
            sx={{ 
              mb: { xs: 3, md: 3 },
              px: { xs: 1, sm: 0 }
            }}>
            Start building your AI-powered business today
          </Typography>
          
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={{ xs: 2, sm: 2, md: 3 }}
            justifyContent='center'
            alignItems='center'
            flexWrap="wrap">
            
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                minWidth: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'center', sm: 'flex-start' },
                p: { xs: 1, sm: 0 }
              }}
            >
              <Box
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  borderRadius: '50%',
                  background: 'rgba(0, 85, 255, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: 14, sm: 16 },
                }}
              >
                ⚡
              </Box>
              <Typography 
                fontWeight={500}
                fontSize={{ xs: 14, sm: 16 }}
              >
                Setup in 15 minutes
              </Typography>
            </Box>
            
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                minWidth: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'center', sm: 'flex-start' },
                p: { xs: 1, sm: 0 }
              }}
            >
              <Box
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  borderRadius: '50%',
                  background: 'rgba(40, 167, 69, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: 14, sm: 16 },
                }}
              >
                💰
              </Box>
              <Typography 
                fontWeight={500}
                fontSize={{ xs: 14, sm: 16 }}
              >
                Start completely free
              </Typography>
            </Box>
            
            <Box 
              sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 1,
                minWidth: { xs: '100%', sm: 'auto' },
                justifyContent: { xs: 'center', sm: 'flex-start' },
                p: { xs: 1, sm: 0 }
              }}
            >
              <Box
                sx={{
                  width: { xs: 28, sm: 32 },
                  height: { xs: 28, sm: 32 },
                  borderRadius: '50%',
                  background: 'rgba(255, 107, 53, 0.12)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: { xs: 14, sm: 16 },
                }}
              >
                🚀
              </Box>
              <Typography 
                fontWeight={500}
                fontSize={{ xs: 14, sm: 16 }}
              >
                Grow with word-of-mouth
              </Typography>
            </Box>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}

const statsData = [
  {
    id: 1,
    icon: '🔍',
    value: 97,
    symbol: 'B',
    sign: '',
    title: 'Local Searches',
    subtitle: 'Monthly searches for businesses',
    iconBg: 'rgba(0, 85, 255, 0.12)',
    numberColor: '#0055FF',
  },
  {
    id: 2,
    icon: '👥',
    value: 92,
    symbol: '%',
    sign: '',
    title: 'Trust Friends',
    subtitle: 'Over advertising messages',
    iconBg: 'rgba(255, 107, 53, 0.12)',
    numberColor: '#FF6B35',
  },
  {
    id: 3,
    icon: '💬',
    value: 2,
    symbol: 'x',
    sign: '',
    title: 'Word-of-Mouth',
    subtitle: 'More sales than paid ads',
    iconBg: 'rgba(156, 39, 176, 0.12)',
    numberColor: '#9C27B0',
  },
  {
    id: 4,
    icon: '🤖',
    value: 80,
    symbol: '%',
    sign: '',
    title: 'AI Automation',
    subtitle: 'Tasks handled automatically',
    iconBg: 'rgba(40, 167, 69, 0.12)',
    numberColor: '#28A745',
  },
];